<script>
import Common from '../../common/Common.vue'
import Endpoints from '../../common/Endpoints.vue'
import Request from '../../common/Request.vue'
import Services from '../../common/Services.vue'
import { companySettings } from '../../types/companySettings'
import { createDefaultMonitorEventFilters, getDefaultMonitorEventFilterOptions, normalizeMonitorEventFilters } from '../../utils/events'

export default {
    init: async payload => {
        await payload.getMe()
    },
    methods: {
        browserDarkMode() {
            return window.matchMedia('(prefers-color-scheme: dark)').matches
        },
        async getMe() {
            await Request.do(
                this,
                'get',
                Request.getDefaultHeader(this),
                {},
                `${Endpoints.systemUsers.getMe}`,
                async response => {
                    try {
                        if (!response) {
                            return
                        }

                        this.user = response?.result
                        this.company = response?.result?.company || {}
                        this.role = response?.result?.companyUser?.subtype || ''
                        this.darkMode = this.user?.darkMode === true

                        if (!this.company.reportConfigs) {
                            this.company.reportConfigs = []
                        }

                        if (this.darkMode) {
                            document.body.classList.add('dark-mode')
                            this.$registerEvent.$emit('setDarkMode', true)
                        } else {
                            document.body.classList.remove('dark-mode')
                            this.$registerEvent.$emit('setDarkMode', false)
                        }

                        if (this.role === 'ADMIN') {
                            await this.loadMonitorEventFilterOptions()
                            await this.loadCompanySettings()
                        } else {
                            this.companySettings = JSON.parse(JSON.stringify(companySettings))
                            this.monitorEventFilterOptions = getDefaultMonitorEventFilterOptions()
                            this.monitorEventFilters = createDefaultMonitorEventFilters(this.monitorEventFilterOptions)
                        }
                    } catch (_error) {
                        /* handle getMe error */
                    } finally {
                        this.isLoading = false
                    }
                },
                () => {
                    this.isLoading = false
                },
            )
        },
        updateDarkMode() {
            const user = { ...this.user, darkMode: this.darkMode === true }
            const data = { darkMode: user.darkMode }

            try {
                Request.do(
                    this,
                    'put',
                    Request.getDefaultHeader(this),
                    data,
                    `${Endpoints.systemUsers.selfUpdate}${user._id}`,
                    response => {
                        if (response.status === 200) {
                            this.user = user
                            // getMe returns reference IDs; keep the populated login scope.
                            this.$session.set('user', { ...this.$session.get('user'), darkMode: user.darkMode })
                            this.$registerEvent.$emit('setDarkMode', user.darkMode)
                            Common.show(this, 'bottom-right', 'success', this.$t('str.form.update.success'))
                        }
                    },
                    () => {
                        Common.show(this, 'bottom-right', 'warn', this.$t('str.form.update.generic.error'))
                    },
                )
            } catch (_error) {
                Common.show(this, 'bottom-right', 'warn', this.$t('str.form.update.generic.error'))
            }
        },
        getCurrentCompanySettingsAccount() {
            return this.companySettings?.account || this.user?.account?._id || ''
        },
        async loadMonitorEventFilterOptions() {
            try {
                const options = await Services.getCompanySettingsEventTypes(this)
                this.monitorEventFilterOptions = Array.isArray(options) && options.length > 0 ? options : getDefaultMonitorEventFilterOptions()
            } catch (_error) {
                this.monitorEventFilterOptions = getDefaultMonitorEventFilterOptions()
            }

            this.monitorEventFilters = createDefaultMonitorEventFilters(this.monitorEventFilterOptions)
        },
        async loadCompanySettings() {
            this.isLoadingCompanySettings = true

            try {
                const settings = await Services.getMyCompanySettings(this)
                const account = settings?.account || this.user?.account?._id || ''
                this.companySettings = {
                    ...JSON.parse(JSON.stringify(companySettings)),
                    ...settings,
                    account: settings?.account || account,
                    monitor: {
                        ...JSON.parse(JSON.stringify(companySettings.monitor)),
                        ...(settings?.monitor || {}),
                        callRecordingEnabled: settings?.monitor?.callRecordingEnabled === true,
                    },
                }
                this.monitorEventFilters = normalizeMonitorEventFilters(this.companySettings?.monitor?.eventFilters, this.monitorEventFilterOptions)
            } catch (_error) {
                const account = this.user?.account?._id || ''
                this.companySettings = {
                    ...JSON.parse(JSON.stringify(companySettings)),
                    account,
                }
                this.monitorEventFilters = createDefaultMonitorEventFilters(this.monitorEventFilterOptions)
            } finally {
                this.isLoadingCompanySettings = false
            }
        },
        setMonitorEventFilterState(type, enabled) {
            this.monitorEventFilters = this.monitorEventFilters.map(item => {
                if (item.type !== type) {
                    return item
                }

                return {
                    ...item,
                    enabled,
                }
            })
        },
        async handleMonitorEventFilterChange(type, enabled) {
            if (!this.canEditMonitorEventFilters || this.isSavingCompanySettings || !this.hasCompanySettingsAccount) {
                return
            }

            const previousFilters = JSON.parse(JSON.stringify(this.monitorEventFilters))
            this.setMonitorEventFilterState(type, enabled)

            try {
                await this.saveCompanySettings()
            } catch (_error) {
                this.monitorEventFilters = previousFilters
            }
        },
        async saveCompanySettings() {
            if (!this.canEditMonitorEventFilters) {
                return false
            }

            const account = this.getCurrentCompanySettingsAccount()
            if (!account) {
                return false
            }

            this.isSavingCompanySettings = true

            try {
                const savedSettings = await Services.updateCompanySettings(this, {
                    status: this.companySettings?.status || 'ACTIVE',
                    monitor: {
                        eventFilters: this.monitorEventFilters.map(item => ({
                            type: item.type,
                            enabled: item.enabled === true,
                        })),
                        callRecordingEnabled: this.companySettings?.monitor?.callRecordingEnabled === true,
                    },
                })

                this.companySettings = {
                    ...JSON.parse(JSON.stringify(companySettings)),
                    ...savedSettings,
                    account: savedSettings?.account || account,
                    monitor: {
                        ...JSON.parse(JSON.stringify(companySettings.monitor)),
                        ...(savedSettings?.monitor || {}),
                        callRecordingEnabled: savedSettings?.monitor?.callRecordingEnabled === true,
                    },
                }
                this.monitorEventFilters = normalizeMonitorEventFilters(this.companySettings?.monitor?.eventFilters, this.monitorEventFilterOptions)
                return true
            } catch (error) {
                Common.show(this, 'bottom-right', 'warn', this.$t('str.form.update.generic.error'))
                throw error
            } finally {
                this.isSavingCompanySettings = false
            }
        },
        handleSetDarkMode: function (e) {
            const value = e.target.checked
            if (value === true) {
                document.body.classList.add('dark-mode')
                this.$registerEvent.$emit('setDarkMode', true)
            } else {
                document.body.classList.remove('dark-mode')
                this.$registerEvent.$emit('setDarkMode', false)
            }

            this.updateDarkMode()
        },
        async handleCallRecordingToggle(enabled) {
            if (!this.canEditMonitorEventFilters || this.isSavingCompanySettings || !this.hasCompanySettingsAccount) {
                return
            }

            const previousValue = this.companySettings?.monitor?.callRecordingEnabled === true
            this.companySettings = {
                ...(this.companySettings || {}),
                monitor: {
                    ...(this.companySettings?.monitor || {}),
                    callRecordingEnabled: enabled === true,
                },
            }

            try {
                await this.saveCompanySettings()
            } catch (_error) {
                this.companySettings = {
                    ...(this.companySettings || {}),
                    monitor: {
                        ...(this.companySettings?.monitor || {}),
                        callRecordingEnabled: previousValue,
                    },
                }
            }
        },
    },
}
</script>
